import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'
import useDebug from 'hooks/useDebug'

import TopNav, {breadcrumbsStore, linksStore} from 'appBase/TopNav'
import Footer from 'appBase/Footer'
import withConfirmation from 'hocs/withConfirmation'
import withUriParams from 'hocs/withUriParams'
import NamedBlock from 'components/blocks/NamedBlock'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'
import LicenseView from './views/LicenseView'
import EventCard from './views/components/EventCard'
import entityUtils from 'utils/entityUtils'
import useSmartNavigate from 'hooks/useSmartNavigate'
import {WORLD_REGION_CODE} from 'constants/constants'

import {queryEvents} from './data/eventQueries'


const EventApp = compose(
    withUriParams([
        ['licenseId', Number]]),
    queryEvents,
    withConfirmation,
    withInitialDataLoadWaiting(['events'])
)((props) => {
    useDebug("EventApp", props);

    const {licenseId, loading} = props;
    const {t} = useTranslation();
    const navigate = useSmartNavigate();

    // preparing links

    // initial breadcrumbs
    const containerId = null;
    breadcrumbsStore.register(containerId);
    linksStore.register(containerId);
    const breadcrumbs = [];
    breadcrumbs.push({
        name: t('appEvent.EventApp.mainPage'),
        to: props.uri,
        back: 1,
        nonBlocking: true });

    useEffect(() => {
        const isTerminalBreadcrumbs = !licenseId;
        breadcrumbsStore.set(containerId, breadcrumbs, isTerminalBreadcrumbs);
        linksStore.set(containerId, [], isTerminalBreadcrumbs);
    });

    // parsing JSON
    const events = useMemo(() =>
        props.events &&
        props.events.filter(e => {
            //we take all events of this license regardless of the user's region
            if (licenseId) {
                return e.license.id === licenseId;
            }
            //else: (main page) license from the same region or world-open license
            const regionCodes = e.license.regions.map(r => r.code);

            return [props.user.regionCode, WORLD_REGION_CODE].some(c => regionCodes.includes(c));
        }).map((event) => ({
            ...event,
            competitionRules: entityUtils.safeJSONParse(event.competitionRules)
        })), [props.events, props.user, licenseId]);

    // creating blocks

    let blocks = [];
    const eventsByLicenseId = {};

    events.forEach(e => {
        eventsByLicenseId[e.license.id] = eventsByLicenseId[e.license.id] || [];
        eventsByLicenseId[e.license.id].push(e);
    });

    blocks = Object.values(eventsByLicenseId)
        .sort(entityUtils.sortByFieldDesc('number', block => block[0].license))
        .sort(entityUtils.sortByFieldDesc('priority', block => block[0].license))
        .map((block , i) => {
            const rows = [];

            let cardIndex = 0;
            const isOddCardsNumber =  block.length % 2 !== 0;

            for (let j = 0; j < Math.ceil(block.length / 2); j++) {
                rows.push(
                    <div className="row mb-1" key={j}>
                        <EventCard hasLine event={block[cardIndex]} user={props.user} uri={`${props.uri}/${block[0].license.id}`} isLarge={isOddCardsNumber && cardIndex === 0} />

                        {block[cardIndex + 1] && (!isOddCardsNumber || cardIndex !== 0) &&
                        <EventCard hasLine event={block[cardIndex + 1]} user={props.user} uri={`${props.uri}/${block[0].license.id}`} />}
                    </div>
                );
                cardIndex += (isOddCardsNumber && cardIndex === 0) ? 1 : 2;
            }

            const onClick = !licenseId && ((e) =>
                navigate(`${props.uri}/${block[0].license.id}`));

            return (
                <NamedBlock key={i} name={block[0].license.organizerName} nameClassName="mb-3 fs-4 text-center text-dark pointer" onClick={onClick} notHighlightImages>
                    {rows}
                </NamedBlock>
            );
        });

    return (
        <div className="d-flex flex-column min-vh-100">
            <TopNav user={props.user} regions={props.regions} skipRootPath={props.uri} executeWithLock={props.executeWithLock} />

            <div className="flex-grow-1">
                {!licenseId &&
                <div className="flex-grow d-flex flex-column scroll-parent">
                    <div className="flex-grow d-flex scroll-parent">
                        <div className="flex-grow scroll">
                            <h5 className="mt-3 fs-4 text-center text-dark">{t('appEvent.EventApp.allEvents')}</h5>
                            <div className="container">
                                {loading.events && t('appEvent.EventApp.loading')}
                                {!loading.events && events.length === 0 && t('appEvent.EventApp.noAccess')}

                                {blocks}

                            </div>
                        </div>
                    </div>
                </div>}

                {licenseId &&
                <LicenseView licenseId={licenseId}
                             block={blocks[0]}
                             showBackLink
                             noAccess={!loading.events && events.length === 0}
                             user={props.user} errors={props.errors} loading={loading} refetch={props.refetch}
                             setActionLockWithMessageAndActions={props.setActionLockWithMessageAndActions}
                             resetActionLock={props.resetActionLock}
                             executeWithLock={props.executeWithLock}
                             executeWithConfirmation={props.executeWithConfirmation}
                             uri={`${props.uri}/${licenseId}`} uriParams={props.uriParams} location={props.location} />}
            </div>

            <Footer />
        </div>
    );
});

export default EventApp;